import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isRepoUser } from "../../../utils/permissions";
import useAuth from "../../../hooks/useAuth";
import useIsMobile from "../../../hooks/useIsMobile";
import FindVehiclesStatsPanel from "../../../components/findVehicles/FindVehiclesStatsPanel";
import { repoCaseService } from "../../../services/repoCase.service";
import confirmationService from "../../../services/confirmation.service";
import { repoUserService } from "../../../services/repoUser.service";
import bankService from "../../../services/bank.service";
import { readPagination } from "../../../utils/listPagination";
import "../../../styles/dashboard.css";

function formatHomeCount(value, ready) {
  if (!ready) return "…";
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en-IN");
}

export default function Home() {
  const { auth } = useAuth();
  const isMobile = useIsMobile();
  const isRepoAdmin = auth?.user?.role === "REPO_ADMIN";
  const isFieldUser = isRepoUser(auth?.user?.role) && !isRepoAdmin;
  const permissions = Array.isArray(auth?.user?.permissions) ? auth.user.permissions : [];
  const canViewUsers = isRepoAdmin || permissions.includes("users.view");
  const hideAdminStarterCards = isMobile && isRepoAdmin;
  const isRepoStaff = isRepoUser(auth?.user?.role);

  const showHeroIntro = isRepoStaff && !auth?.user?.phone;
  const showCancelCard = !hideAdminStarterCards;
  const [homeCounts, setHomeCounts] = useState({
    users: null,
    banks: null,
    cancelled: null,
    ready: false,
    cancelReady: false,
  });

  useEffect(() => {
    if (!auth?.token) return;
    repoCaseService.warmSearchCache(auth.token).catch(() => {});
  }, [auth?.token]);

  useEffect(() => {
    if (!auth?.token || !isRepoAdmin) return undefined;
    let cancelled = false;

    (async () => {
      const [usersRes, banksRes] = await Promise.allSettled([
        repoUserService.getUsers(auth.token, { page: 1, limit: 1 }),
        bankService.getConnectedBanks(),
      ]);
      if (cancelled) return;

      const users =
        usersRes.status === "fulfilled" ? readPagination(usersRes.value).total : null;
      const bankPayload =
        banksRes.status === "fulfilled"
          ? banksRes.value?.data?.data || banksRes.value?.data || {}
          : null;
      const banks =
        bankPayload == null
          ? null
          : Number(bankPayload.total ?? bankPayload.banks?.length ?? 0);

      setHomeCounts((prev) => ({
        ...prev,
        users,
        banks: Number.isFinite(banks) ? banks : null,
        ready: true,
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [auth?.token, isRepoAdmin]);

  useEffect(() => {
    if (!auth?.token || hideAdminStarterCards) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const res = await confirmationService.getAll(auth.token, {
          status: "REJECTED",
          page: 1,
          limit: 1,
        });
        if (cancelled) return;
        setHomeCounts((prev) => ({
          ...prev,
          cancelled: readPagination(res).total,
          cancelReady: true,
        }));
      } catch {
        if (!cancelled) {
          setHomeCounts((prev) => ({ ...prev, cancelled: null, cancelReady: true }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [auth?.token, hideAdminStarterCards]);

  return (
    <div className="content home-page">
      <section
        className={`home-hero${isRepoStaff ? " home-hero--field-user" : ""}${
          !showHeroIntro ? " home-hero--no-intro" : ""
        }`}
        aria-label="Dashboard overview"
      >
        {showHeroIntro && (
          <div className="home-hero__intro">
            <p className="muted home-hero__hint">
              Add your mobile in <Link to="/profile">Profile</Link> so trace reports
              and bank notify show your contact number.
            </p>
          </div>
        )}
        {isRepoStaff && (
          <FindVehiclesStatsPanel
            className="home-field-stats"
            returnPathFallback="/home"
            arrangeHome
            homeSlots={{
              afterConfirmed: showCancelCard ? (
                <Link to="/confirmation?status=REJECTED" className="dashboard-card-link">
                  <div className="card action-card dashboard-action-card">
                    <div className="action-icon">🚫</div>
                    <div className="stat-value home-starter-count" aria-label="Cancelled confirmation count">
                      {formatHomeCount(homeCounts.cancelled, homeCounts.cancelReady)}
                    </div>
                    <h4>Cancel confirmation</h4>
                    <p className="muted small">Cancelled vehicle traces</p>
                  </div>
                </Link>
              ) : null,
              afterInventory: (
                <>
                  {canViewUsers ? (
                    <Link to="/users" className="dashboard-card-link">
                      <div className="card action-card dashboard-action-card">
                        <div className="action-icon">👥</div>
                        {isRepoAdmin ? (
                          <div className="stat-value home-starter-count" aria-label="User count">
                            {formatHomeCount(homeCounts.users, homeCounts.ready)}
                          </div>
                        ) : null}
                        <h4>All users</h4>
                        <p className="muted small">Open Users menu</p>
                      </div>
                    </Link>
                  ) : null}
                  {isRepoAdmin ? (
                    <Link to="/bank-records" className="dashboard-card-link">
                      <div className="card action-card dashboard-action-card">
                        <div className="action-icon">🏦</div>
                        <div className="stat-value home-starter-count" aria-label="Linked bank count">
                          {formatHomeCount(homeCounts.banks, homeCounts.ready)}
                        </div>
                        <h4>Linked Banks / NBFCs</h4>
                        <p className="muted small">Banks / NBFCs linked to your agency</p>
                      </div>
                    </Link>
                  ) : null}
                </>
              ),
            }}
          />
        )}
      </section>
      {!isRepoStaff && (canViewUsers || isRepoAdmin || showCancelCard) ? (
        <section className="home-actions" aria-label="Quick actions">
          <div
            className={`starter-cards home-starter-cards${
              isMobile && isFieldUser ? " home-starter-cards--mobile-user" : ""
            }`}
          >
            {canViewUsers ? (
              <Link to="/users" className="dashboard-card-link">
                <div className="card action-card dashboard-action-card">
                  <div className="action-icon">👥</div>
                  {isRepoAdmin ? (
                    <div className="stat-value home-starter-count" aria-label="User count">
                      {formatHomeCount(homeCounts.users, homeCounts.ready)}
                    </div>
                  ) : null}
                  <h4>All users</h4>
                  <p className="muted small">Open Users menu</p>
                </div>
              </Link>
            ) : null}
            {isRepoAdmin ? (
              <Link to="/bank-records" className="dashboard-card-link">
                <div className="card action-card dashboard-action-card">
                  <div className="action-icon">🏦</div>
                  <div className="stat-value home-starter-count" aria-label="Linked bank count">
                    {formatHomeCount(homeCounts.banks, homeCounts.ready)}
                  </div>
                  <h4>Linked Banks / NBFCs</h4>
                  <p className="muted small">Banks / NBFCs linked to your agency</p>
                </div>
              </Link>
            ) : null}
            {showCancelCard ? (
              <Link to="/confirmation?status=REJECTED" className="dashboard-card-link">
                <div className="card action-card dashboard-action-card">
                  <div className="action-icon">🚫</div>
                  <div className="stat-value home-starter-count" aria-label="Cancelled confirmation count">
                    {formatHomeCount(homeCounts.cancelled, homeCounts.cancelReady)}
                  </div>
                  <h4>Cancel confirmation</h4>
                  <p className="muted small">Cancelled vehicle traces</p>
                </div>
              </Link>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}
